import { API } from 'aws-amplify';
import { useEffect, useState, useRef } from "react";
import { Alert, Button, Col, Form, Row, Spinner, Badge, InputGroup, Table } from "react-bootstrap";
import { Link } from "@aws-amplify/ui-react";
import { useStateStore } from "../AppState";
import CompensationGrid from "../CommonComponents/CompensationGrid/CompensationGrid";
import UploadModal from '../CommonComponents/UploadModal/UploadModal';
import { checkIfCurrentUserIsAdmin, getAuthToken, USER_GROUPS } from "../utils/AuthUtils";
import { prepareWebSocketConnection } from '../utils/WebSocketUtils';
import "./styles.scss";
import { formatTickers } from '../Constants';
import ToolTipWrapper from '../CommonComponents/ToolTipComponent/ToolTipWrapper';
import BulkTickerInputModal from '../ExploreCompensationDataPage/BulkTickerInputModal/BulkTickerInputModal';
import TickerLookupModal from '../ExploreCompensationDataPage/TickerLookupModal/TickerLookupModal';
import SurveyDownloadOutputModal from './SurveyDownloadOutputModal/SurveyDownloadOutputModal';
import TickerNameConflictModal from '../ExploreCompensationDataPage/TickerNameConflictModal/TickerNameConfictModal';
import SurveyRegressionPreviewModal from './SurveyRegressionPreviewModal/SurveyRegressionPreviewModal';
import SurveyDataPointsPreviewModal from './SurveyDataPointsPreviewModal/SurveyDataPointsPreviewModal';
import GroupVisibleComponent from '../GroupVisibleComponent';
import ClientRequestModal from './ClientRequestModal/ClientRequestModal';
import BenchmarkPreviewModal from './BenchmarkPreviewModal/BenchmarkPreviewModal';
import ViewCompanyListModal from '../CommonComponents/CompensationGrid/ViewCompanyListModal/ViewCompanyListModal';

const defaultSurveyYear = 2026;
const REVENUE = "revenue";
const MARKET_CAP = "marketCap";
const TOTAL_ASSETS = "totalAssets";
const maxUpperBound = 9999999999999;
const outputTemplateId = "10e99dd2-a582-4fd4-b123-6d2549dd3ec3";

const finalResultReleaseDate = new Date(2026, 8, 24);
const today = new Date();

const SurveyDataConsole = () => {
    const [surveyData, setSurveyData] = useState([]);
    const [selectedSurveyRows, setSelectedSurveyRows] = useState(new Set());

    const [selectedSurveyTemplateSummary, setSelectedSurveyTemplateSummary] = useState(null);
    const [selectedSurveyTemplateId, setSelectedSurveyTemplateId] = useState(null);
    const [surveyDataGridColumns, setSurveyDataGridColumns] = useState([]);
    const [revenueColumnId, setRevenueColumnId] = useState(null);
    const [marketcapColumnId, setMarketCapColumnId] = useState(null);
    const [totalAssetsColumnId, setTotalAssetsColumnId] = useState(null);
    const [selectedRevenueValues, setSelectedRevenueValues] = useState([]);
    const [selectedMarketCapValues, setSelectedMarketCapValues] = useState([]);
    const [selectedTotalAssetsValues, setSelectedTotalAssetsValues] = useState([]);

    const [isFormValidated, setIsFormValidated] = useState(false);

    const { setLoadingStatus } = useStateStore();
    const [searching, setSearching] = useState(false);
    const [downloadingInput, setDownloadingInput] = useState(false);

    const [error, setError] = useState(null);
    const [successMessage, setSuccess] = useState(null);

    const [showUploadModal, setShowUploadModal] = useState(false);
    const [isAdminUser, setIsAdminUser] = useState(false);

    const [gridExpandedStatus, setGridExpandedStatus] = useState(false);

    const [excludeDefaultBreakouts, setExcludeDefaultBreakouts] = useState(false);

    const [tickers, setTickers] = useState([])
    const [showBulkTickerInputModal, setShowBulkTickerInputModal] = useState(false);
    const [showTickerLookupModal, setShowTickerLookupModal] = useState(false);
    const [showSurveyDownloadOutputModal, setShowSurveyDownloadOutputModal] = useState(false);
    const [downloadType, setDownloadType] = useState(null);
    const [showClientRequestModal, setShowClientRequestModal] = useState(false);
    const [clientRequestType, setClientRequestType] = useState(null);
    const [primaryFilterType, setPrimaryFilterType] = useState("");
    const [primaryLowerBound, setPrimaryLowerBound] = useState(0);
    const [primaryUpperBound, setPrimaryUpperBound] = useState(maxUpperBound);
    const [primaryLowerBoundDisplay, setPrimaryLowerBoundDisplay] = useState("");
    const [primaryUpperBoundDisplay, setPrimaryUpperBoundDisplay] = useState("");
    const [filterOperator, setFilterOperator] = useState("");
    const [secondaryFilterType, setSecondaryFilterType] = useState("MARKET_CAP");
    const [secondaryLowerBound, setSecondaryLowerBound] = useState(0);
    const [secondaryUpperBound, setSecondaryUpperBound] = useState(maxUpperBound);
    const [secondaryLowerBoundDisplay, setSecondaryLowerBoundDisplay] = useState("");
    const [secondaryUpperBoundDisplay, setSecondaryUpperBoundDisplay] = useState("");
    const [primaryFilterInvalid, setPrimaryFilterInvalid] = useState(false);
    const [secondaryFilterInvalid, setSecondaryFilterInvalid] = useState(false);
    const [noDataAvailable, setNoDataAvailable] = useState([]);
    const [showTickerNameConflictModal, setShowTickerNameConflictModal] = useState(false);
    const [hasConflictingTicker, setHasConflictingTicker] = useState(false);
    const [selectedConflictingCompanyIds, setSelectedConflictingCompanyIds] = useState([]);
    const [conflictingCompanies, setConflictingCompanies] = useState([]);

    const [showViewCompanyListModal, setShowViewCompanyListModal] = useState(false);
    const [showSurveyRegressionPreviewModal, setShowSurveyRegressionPreviewModal] = useState(false);
    const [showSurveyDataPointsPreviewModal, setShowSurveyDataPointsPreviewModal] = useState(false);
    const [showBenchmarkPreviewModal, setShowBenchmarkPreviewModal] = useState(false);
    const [selectedRegressionType, setSelectedRegressionType] = useState("");
    const [surveyYear, setSurveyYear] = useState(defaultSurveyYear);
    const [downloadingDoc, setDownloadingDoc] = useState(false);
    const [finalResultReleased, setFinalResultReleased] = useState(today > finalResultReleaseDate);
    const downloadLinkRef = useRef(null);
    const formRef = useRef(null);

    const handleSettingGridColumns = async (data) => {
        let colDefinitions;
        const isAdmin = await checkIfCurrentUserIsAdmin();
        if (isAdmin) {
            colDefinitions = data.summaryColDefinitions;
        } else {
            colDefinitions = data.summaryColDefinitions.filter(colDef => !colDef.isSensitive);
        }
        setSurveyDataGridColumns(colDefinitions);
    }

    const handleSettingFinancialColumndIds = (data) => {
        const colDefinitions = data.summaryColDefinitions;
        for (const colDef of colDefinitions) {
            if (colDef.name.includes("Revenue")) {
                setRevenueColumnId(colDef.id);
            } else if (colDef.name.includes("Market Cap")) {
                setMarketCapColumnId(colDef.id);
            } else if (colDef.name.includes("Total Assets")) {
                setTotalAssetsColumnId(colDef.id);
            }
        }
    }

    const handleShowRegressionPreviewModal = (regressionType) => {
        setSelectedRegressionType(regressionType);
        setShowSurveyRegressionPreviewModal(true);
    }

    const handleSettingTemplateData = async (data) => {
        setSelectedSurveyTemplateId(data.templateId)
        setSelectedSurveyTemplateSummary(data);
        await handleSettingGridColumns(data);
        handleSettingFinancialColumndIds(data);
    }

    const handleSearchingData = async (e) => {
        e.preventDefault();
        if (e.target.checkValidity() === false) {
            e.stopPropagation();
            setIsFormValidated(true);
            return;
        }

        if ((primaryUpperBound < primaryLowerBound) || primaryUpperBound < 0 || primaryLowerBound < 0) {
            setPrimaryFilterInvalid(true);
            return;
        }

        if ((secondaryUpperBound < secondaryLowerBound) || secondaryUpperBound < 0 || secondaryLowerBound < 0) {
            setSecondaryFilterInvalid(true);
            return;
        }

        await retrieveSurveyData();
    }

    const retrieveSurveyData = async () => {
        setSearching(true);
        setSurveyData([]);
        setNoDataAvailable([]);
        setSuccess(null);
        setError(null);
        setPrimaryFilterInvalid(false);
        setSecondaryFilterInvalid(false);

        const queryStringParameters = {
            templateId: selectedSurveyTemplateId,
            surveyYear: surveyYear,
            companyIds: tickers.join(","),
            primaryFilterType,
            primaryLowerBound,
            primaryUpperBound
        }

        if (filterOperator && secondaryFilterType) {
            queryStringParameters["filterOperator"] = filterOperator;
            queryStringParameters["secondaryFilterType"] = secondaryFilterType;
            queryStringParameters["secondaryLowerBound"] = secondaryLowerBound;
            queryStringParameters["secondaryUpperBound"] = secondaryUpperBound;
        }

        if (selectedConflictingCompanyIds.length > 0) {
            queryStringParameters["selectedConflictingCompanyIds"] = selectedConflictingCompanyIds.join(",");
        }

        const authToken = await getAuthToken();
        API.get('AnnualCompensations', "/survey/compensations", {
            headers: {
                'Token': authToken
            },
            queryStringParameters
        }).then((response) => {
            setSearching(false);
            if (response?.hasConflictingTickers) {
                handleConflictingCompanies(response);
            }
            if (response?.data?.length > 0) {
                setSurveyData(response.data);
                setNoDataAvailable(response.noDataAvailable);

                setError(null);
            } else {
                setError("No Survey data found for the selected tickers and filter");
            }
        }).catch((error) => {
            setSearching(false);
            if (error !== undefined && error.response !== undefined &&
                error.response.data !== undefined && error.response.data.message !== undefined) {
                setError(error.response.data.message);
            }
        });
    }

    const handleInputDataDownload = async () => {
        setSuccess(null);
        if (selectedSurveyRows.size === 0) {
            setError(true);
            setError("Please select at least one row to download");
            return;
        }
        setError(null);
        setDownloadingInput(true);

        const selecteCompIds = [...selectedSurveyRows];
        const request = {
            requestType: "Survey_Input",
            selectedCompensationIds: selecteCompIds,
            templateId: selectedSurveyTemplateId
        }

        try {
            await prepareWebSocketConnection(request, handleSuccessInput, handleErrorInput);
        } catch (error) {
            setDownloadingInput(false);
            if (error?.response?.data?.message) {
                setError(error.response.data.message);
            } else {
                setError("An error occurred while downloading survey input data. Please try again later.");
            }
        }
    }

    const handleOpenSurveyDownloadModal = (type) => {
        setDownloadType(type);
        setShowSurveyDownloadOutputModal(true);
    }

    const handleCloseSurveyDownloadModal = () => {
        setDownloadType(null);
        setShowSurveyDownloadOutputModal(false);
    }

    const handleOpenClientRequestModal = (type) => {
        setClientRequestType(type);
        setShowClientRequestModal(true);
    }

    const handleCloseClientRequestModal = () => {
        setClientRequestType(null);
        setShowClientRequestModal(false);
    }

    const handleShowBenchmarkPreviewModal = () => {
        setShowBenchmarkPreviewModal(true);
    }

    const handleConflictingCompanyIdSelection = (companyIds) => {
        const selectedConflictingCompanies = conflictingCompanies.filter(company => companyIds.includes(company.exchangeTicker));
        const selectedExchangeTickers = selectedConflictingCompanies.map(company => company.exchangeTicker);
        setSelectedConflictingCompanyIds(selectedExchangeTickers);
    }

    const handleConflictingCompanies = (response) => {
        const conflictingCompanies = response.conflictingTickers;
        setConflictingCompanies(conflictingCompanies);
        setHasConflictingTicker(true);
        setShowTickerNameConflictModal(true);
    }

    const handleErrorInput = (error) => {
        setDownloadingInput(false);
        setError(error);
    }

    const handleSuccessInput = (url) => {
        handleDownloadUrl(url);
        setDownloadingInput(false);
    }

    const handleDownloadUrl = (url) => {
        const link = document.createElement('a');
        link.href = url;
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
    }

    const handleArrayTickers = (tickers) => {
        const uniqueTickers = [...new Set(tickers)];
        const formattedTickers = uniqueTickers.map(ticker => formatTickers(ticker));
        if (formattedTickers && formattedTickers.length > 0) {
            const flattenedTickers = [...new Set(formattedTickers.flat())];
            return flattenedTickers;
        }
        return [];
    }

    const handleStringTickers = (tickers) => {
        const rawTickers = tickers.split(",").map(ticker => ticker.trim().toUpperCase());
        const uniqueTickers = [...new Set(rawTickers)];
        const formattedTickers = uniqueTickers.map(ticker => formatTickers(ticker));
        if (formattedTickers && formattedTickers.length > 0) {
            return formattedTickers;
        }
        return [];
    }

    const handleParsingTickers = (tickers) => {
        if (!tickers || tickers.length === 0 || tickers === "") {
            return [];
        }

        if (Array.isArray(tickers)) {
            return handleArrayTickers(tickers);
        }

        if (typeof tickers === 'string' && tickers.includes(",")) {
            return handleStringTickers(tickers);
        }

        return [tickers.toUpperCase()];
    }

    const handleSettingTickers = (tickerList) => {
        const parsedTickers = handleParsingTickers(tickerList)
        setTickers(parsedTickers);
    }

    const handlePastedTickers = (e) => {
        e.preventDefault();
        const pastedTickers = e.clipboardData.getData('text').split(",");
        if (pastedTickers.length === 1) {
            handleSettingTickers(formatTickers(pastedTickers[0]));
            return;
        }
        handleSettingTickers(formatTickers(pastedTickers));
    }

    const handleSearchBulkTickers = async (unParsedTickers) => {
        if (formRef.current !== null) {
            formRef.current.reportValidity();
            formRef.current.classList.add("was-validated");
        }
        if (unParsedTickers === undefined || unParsedTickers === null || unParsedTickers === "" || unParsedTickers.length === 0) {
            handleParsingTickers([]);
            return;
        }
        if (formRef.current !== null) {
            formRef.current.classList.remove("was-validated");
        }
        setTickers(handleParsingTickers(unParsedTickers));

        await handleSearchingData();
    }

    const handleSetPrimaryFilterType = (value) => {
        if (value === "") {
            setFilterOperator("");
            setSecondaryFilterInvalid("");
            setSecondaryLowerBound(0);
            setSecondaryUpperBound(maxUpperBound);
            setSecondaryLowerBoundDisplay("");
            setSecondaryUpperBoundDisplay("");
            setPrimaryUpperBound(maxUpperBound);
            setPrimaryLowerBound(0);
            setPrimaryUpperBoundDisplay("");
            setPrimaryLowerBoundDisplay("");
        }
        setPrimaryFilterType(value);
    }

    const handleSetFilterOperator = (value) => {
        if (value === "") {
            setSecondaryLowerBound(0);
            setSecondaryUpperBound(maxUpperBound);
            setSecondaryLowerBoundDisplay("");
            setSecondaryUpperBoundDisplay("");
        }
        setFilterOperator(value);
    }

    const handleNumberInput = (value) => {
        let val = parseInt(value);
        val = val >= 0 ? val : 0;
        return val;
    }
    const handleSetPrimaryUpperBound = (value) => {
        const strValue = value.replace(/,/g, '');
        if (isNaN(Number(strValue))) {
            return;
        }
        const numVal = handleNumberInput(strValue);
        if (numVal === 0) {
            setPrimaryUpperBoundDisplay("")
            setPrimaryUpperBound(maxUpperBound);
        } else {
            setPrimaryUpperBoundDisplay(numVal.toLocaleString())
            setPrimaryUpperBound(numVal);
        }
    }

    const handleSetPrimaryLowerBound = (value) => {
        const strValue = value.replace(/,/g, '');
        if (isNaN(Number(strValue))) {
            return;
        }
        const numVal = handleNumberInput(strValue);
        setPrimaryLowerBoundDisplay(numVal.toLocaleString())
        setPrimaryLowerBound(numVal);
    }

    const handleSetSecondaryUpperBound = (value) => {
        const strValue = value.replace(/,/g, '');
        if (isNaN(Number(strValue))) {
            return;
        }
        const numVal = handleNumberInput(strValue);
        if (numVal === 0) {
            setSecondaryUpperBoundDisplay("")
            setSecondaryUpperBound(maxUpperBound);
        } else {
            setSecondaryUpperBoundDisplay(numVal.toLocaleString())
            setSecondaryUpperBound(numVal);
        }
    }

    const handleSetSecondaryLowerBound = (value) => {
        const strValue = value.replace(/,/g, '');
        if (isNaN(Number(strValue))) {
            return;
        }
        const numVal = handleNumberInput(strValue);
        setSecondaryLowerBoundDisplay(numVal.toLocaleString())
        setSecondaryLowerBound(numVal);
    }

    const handleBringSelectedDataToTop = (compData) => {
        if (selectedSurveyRows.size > 0) {
            const selectedCompIds = [...selectedSurveyRows];
            const selectedCompensationData = compData.filter(comp => selectedCompIds.includes(comp.uniqueId));
            const unSelectedCompensationData = compData.filter(comp => !selectedCompIds.includes(comp.uniqueId));
            const sortedCompensationData = [...selectedCompensationData, ...unSelectedCompensationData];
            setSurveyData(sortedCompensationData);
            setSelectedSurveyRows(new Set(selectedCompIds));
        } else {
            setSurveyData(compData);
        }
    }

    const handleUpdateFinancialValues = () => {
        const revenueVals = [];
        const marketCapVals = [];
        const totalAssetsVals = [];
        const selectedCompensationData = surveyData.filter(comp => selectedSurveyRows.has(comp.uniqueId));
        for (const row of selectedCompensationData) {
            const revenue = Number(row[revenueColumnId]) ?? undefined;
            const marketCap = Number(row[marketcapColumnId]) ?? undefined;
            const totalAssets = Number(row[totalAssetsColumnId]) ?? undefined;
            if (!isNaN(revenue) && revenue !== undefined) {
                revenueVals.push(revenue);
            }
            if (!isNaN(marketCap) && marketCap !== undefined) {
                marketCapVals.push(marketCap);
            }
            if (!isNaN(totalAssets) && totalAssets !== undefined) {
                totalAssetsVals.push(totalAssets);
            }
        }

        revenueVals.sort((a, b) => a - b);
        marketCapVals.sort((a, b) => a - b);
        totalAssetsVals.sort((a, b) => a - b);

        const revenueTableValues = [];
        revenueTableValues.push(getPercentile(revenueVals, 0.75));
        revenueTableValues.push(getMean(revenueVals));
        revenueTableValues.push(getMedianValue(revenueVals));
        revenueTableValues.push(getPercentile(revenueVals, 0.25));

        const marketCapTableValues = [];
        marketCapTableValues.push(getPercentile(marketCapVals, 0.75));
        marketCapTableValues.push(getMean(marketCapVals));
        marketCapTableValues.push(getMedianValue(marketCapVals));
        marketCapTableValues.push(getPercentile(marketCapVals, 0.25));

        const totalAssetsTableValues = [];
        totalAssetsTableValues.push(getPercentile(totalAssetsVals, 0.75))
        totalAssetsTableValues.push(getMean(totalAssetsVals));
        totalAssetsTableValues.push(getMedianValue(totalAssetsVals));
        totalAssetsTableValues.push(getPercentile(totalAssetsVals, 0.25));

        setSelectedRevenueValues(revenueTableValues);
        setSelectedMarketCapValues(marketCapTableValues);
        setSelectedTotalAssetsValues(totalAssetsTableValues);
    }

    const getMedianValue = (arr) => {
        if (!arr || arr.length === 0) {
            return "N/A"
        }

        const length = arr.length;
        const middle = Math.floor(length / 2);
        let val;
        if (length % 2 === 0) {
            val = (arr[middle - 1] + arr[middle]) / 2;
        } else {
            val = arr[middle];
        }
        return Math.round(val).toLocaleString();
    }

    const getPercentile = (arr, percent) => {
        if (!arr || arr.length === 0) {
            return "N/A";
        }
        const position = (arr.length - 1) * percent;
        const upperBoundIndex = Math.ceil(position);
        const lowerBoundIndex = Math.floor(position);

        const difference = position - lowerBoundIndex;

        const val = arr[lowerBoundIndex] + (arr[upperBoundIndex] - arr[lowerBoundIndex]) * difference;

        return Math.round(val).toLocaleString();
    }

    const getMean = (arr) => {
        if (!arr || arr.length === 0) {
            return "N/A"
        }
        const val = arr.reduce((a, b) => a + b) / arr.length;

        return Math.round(val).toLocaleString();
    }

    const fetchSurveyTemplateNames = async () => {
        setLoadingStatus(true);
        const url = '/survey/template-names';
        const authToken = await getAuthToken();
        API.get('AnnualCompensations', url, {
            headers: {
                'Token': authToken
            }
        }).then((response) => {
            setLoadingStatus(false);
            handleSettingTemplateData(response.data[0])
        }).catch((error) => {
            setLoadingStatus(false);
            if (error?.response?.data?.message) {
                setError(error.response.data.message);
            }
        });
    }


    const financialOverviewTable = (!searching && surveyData && surveyData.length > 0 && <div className="financials-table" style={{ paddingTop: "1.75rem" }}>
        <Table striped bordered hover size="sm">
            <thead className="table-light">
                <tr>
                    <th style={{ width: '25%' }} className="fw-semibold small text-uppercase py-2"><b>{selectedSurveyRows.size}</b> Selected Rows</th>
                    <th style={{ width: '25%' }} className="fw-semibold small text-uppercase py-2">Revenue ($M)</th>
                    <th style={{ width: '25%' }} className="fw-semibold small text-uppercase py-2">Market Cap ($M)</th>
                    <th style={{ width: '25%' }} className="fw-semibold small text-uppercase py-2">Total Assets ($M)</th>
                </tr>
            </thead>
            <tbody>
                <tr key="mean">
                    <td>Mean</td>
                    <td>{selectedRevenueValues[1]}</td>
                    <td>{selectedMarketCapValues[1]}</td>
                    <td>{selectedTotalAssetsValues[1]}</td>
                </tr>
                <tr key="seventyFifthPercentile">
                    <td>75th Percentile</td>
                    <td>{selectedRevenueValues[0]}</td>
                    <td>{selectedMarketCapValues[0]}</td>
                    <td>{selectedTotalAssetsValues[0]}</td>
                </tr>
                <tr key="median">
                    <td>Median</td>
                    <td>{selectedRevenueValues[2]}</td>
                    <td>{selectedMarketCapValues[2]}</td>
                    <td>{selectedTotalAssetsValues[2]}</td>
                </tr>
                <tr key="twentyFifthPercentile">
                    <td>25th Percentile</td>
                    <td>{selectedRevenueValues[3]}</td>
                    <td>{selectedMarketCapValues[3]}</td>
                    <td>{selectedTotalAssetsValues[3]}</td>
                </tr>
            </tbody>
        </Table>
    </div>)

    const downloadRelevantDocument = async (docType) => {
        setDownloadingDoc(true);
        const url = '/relevant-documents';
        const authToken = await getAuthToken();
        const queryStringParameters = {
            docType,
            surveyYear,
            templateId: outputTemplateId
        }
        API.get('AnnualCompensations', url, {
            headers: {
                'Token': authToken
            },
            queryStringParameters
        }).then((response) => {
            setDownloadingDoc(false);
            if (downloadLinkRef.current) {
                downloadLinkRef.current.href = response.url;
                downloadLinkRef.current.click();
            }
        }).catch((error) => {
            setDownloadingDoc(false);
            if (error !== undefined && error.response !== undefined &&
                error.response.data !== undefined && error.response.data.message !== undefined) {
                console.log(error.response.data.message);
            } else {
                console.log("An error occurred while retrieving compensation data. Please try again later.");
            }
        });
    }


    useEffect(() => {
        handleUpdateFinancialValues();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSurveyRows]);

    useEffect(() => {
        let isMounted = true;
        if (isMounted) {
            fetchSurveyTemplateNames();
        }
        return () => { isMounted = false };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let isMounted = true;
        if (isMounted) {
            if (selectedSurveyRows.size > surveyData.length) {
                setSelectedSurveyRows(new Set());
            }
        }
        return () => { isMounted = false };
    }, [surveyData, selectedSurveyRows]);

    useEffect(() => {
        let isMounted = true;
        if (isMounted) {
            setSurveyData([]);
            setNoDataAvailable([]);
            setHasConflictingTicker(false);
            setSelectedConflictingCompanyIds([]);
            setConflictingCompanies([]);
            setShowTickerNameConflictModal(false);
            setError(null);
            setSuccess(null);
            setSelectedSurveyRows(new Set());
            checkIfCurrentUserIsAdmin().then((isAdmin) => {
                setIsAdminUser(isAdmin);
                setExcludeDefaultBreakouts(!isAdmin);
            });
            setPrimaryFilterType("");
            setPrimaryLowerBound(0);
            setPrimaryUpperBound(maxUpperBound);
            setFilterOperator("");
            setSecondaryFilterType("MARKET_CAP");
            setSecondaryLowerBound(0);
            setSecondaryUpperBound(maxUpperBound);
            setPrimaryLowerBoundDisplay("");
            setPrimaryUpperBoundDisplay("");
            setSecondaryLowerBoundDisplay("");
            setSecondaryUpperBoundDisplay("");
            setPrimaryFilterInvalid(false);
            setSecondaryFilterInvalid(false);
            setDownloadType(null);
            setShowSurveyRegressionPreviewModal(false);
            setShowSurveyDataPointsPreviewModal(false);
            setShowViewCompanyListModal(false);
            setSelectedRegressionType("");
            setSelectedRevenueValues([]);
            setSelectedMarketCapValues([]);
            setFinalResultReleased(today > finalResultReleaseDate);
            if (selectedSurveyTemplateId) {
                retrieveSurveyData();
            }
        }
        return () => { isMounted = false };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSurveyTemplateId, surveyYear]);

    return (
        <div className="survey-data">
            <div className="console-header survey-data-header d-flex align-items-center justify-content-between">
                <div className="header-title survey-data-header-copy">
                    <div className="survey-data-kicker">EXECUTIVE COMPENSATION INTELLIGENCE</div>
                    <h1>{surveyYear} FW Cook Survey</h1>
                    <p>Build custom survey samples, review market statistics, and prepare benchmarking outputs.</p>
                </div>
                <div className="survey-data-header-actions d-flex gap-2">
                    {finalResultReleased && <>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => downloadRelevantDocument("finalSurveyBenchmarkingResult")}
                            title="Download Final Survey Pay Benchmarking Result"
                        >
                            <i className="fa fa-download me-2" />2026 Final Survey Pay Benchmarking Result
                        </Button>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => downloadRelevantDocument("finalSurveyPolicyResult")}
                            title="Download Final Survey Policies and Design Result"
                        >
                            <i className="fa fa-download me-2" />2026 Final Survey Policies and Design Result
                        </Button>
                    </>}
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => downloadRelevantDocument("surveyMethodology")}
                        title="Download Survey Methodology"
                    >
                        <i className="fa fa-download me-2" />Survey Methodology
                    </Button>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => downloadRelevantDocument("surveyWebinarDeck")}
                        title="Download Webinar Deck"
                    >
                        <i className="fa fa-file-powerpoint me-2" /> 2026 Webinar Deck
                    </Button>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => downloadRelevantDocument("surveyFAQ")}
                        title="Download Survey FAQs"
                    >
                        <i className="fa fa-question me-2" />Survey FAQs
                    </Button>
                    <a href="#self" ref={downloadLinkRef} download id="download-relevant-doc" hidden={true}>Download</a>
                </div>
            </div>
            <div className="divider"></div>
            {downloadingDoc && <div className="loading-backdrop">
                <Spinner animation="border" variant="primary" className="loading-spinner">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>}
            <div className="console-container">
                <Form noValidate validated={isFormValidated} className="survey-data-form" onSubmit={handleSearchingData}>
                    <Row>
                        <Col md={7}>
                            <Row>
                                <Col md={10}>
                                    {selectedSurveyTemplateId && <Form.Group controlId="tickers" className="tickers-input">
                                        <Form.Label>
                                            <span style={{ marginRight: "0.25rem" }}>Tickers (Optional)</span>
                                            <ToolTipWrapper
                                                id="tickers-tooltip"
                                                text="Enter a ticker or multiple tickers separated by commas or new lines. Copy and paste from Excel is also supported."
                                                element={<i className="fa fa-question-circle"></i>}
                                                placement={"top"}
                                            />
                                        </Form.Label>
                                        {tickers.length > 0 ?
                                            <ToolTipWrapper
                                                placement={"top"}
                                                id="number-of-companies-badge"
                                                text="Number of unique tickers"
                                                element={<Badge style={{ fontSize: "0.6rem", marginLeft: "0.25rem" }}
                                                    bg="secondary">{Array.isArray(tickers) ? tickers.length : 1}</Badge>} /> : ""}
                                        <BulkTickerInputModal
                                            initialTickers={tickers}
                                            disabled={searching}
                                            onClose={() => setShowBulkTickerInputModal(false)}
                                            isOpen={showBulkTickerInputModal}
                                            onAddTickers={(formattedTickers) => handleSearchBulkTickers(formattedTickers)}
                                        />
                                        <ViewCompanyListModal
                                            show={showViewCompanyListModal}
                                            onClose={() => setShowViewCompanyListModal(false)}
                                        />
                                        <TickerLookupModal isOpen={showTickerLookupModal} onClose={() => setShowTickerLookupModal(false)} onAddTickers={handleSettingTickers} />
                                        <InputGroup>
                                            <Form.Control
                                                disabled={searching}
                                                className="ticker-base-input"
                                                type="text"
                                                placeholder="e.g. AAPL, MSFT, GOOG, etc."
                                                value={tickers}
                                                onChange={(e) => handleSettingTickers(e.target.value)}
                                                onPaste={(e) => handlePastedTickers(e)}
                                                autoComplete="off"
                                            ></Form.Control>
                                            <InputGroup.Text title="Expand to view all tickers">
                                                <Button className="expand-tickers-view-btn" disabled={searching} onClick={() => setShowBulkTickerInputModal(true)}>
                                                    <i className="fa fa-expand-alt"></i>
                                                </Button>
                                            </InputGroup.Text>
                                            <InputGroup.Text title="View saved company lists">
                                                <Button className="view-saved-companies-lists-btn" title='View saved company lists' onClick={() => setShowViewCompanyListModal(true)}>
                                                    <i className="fa fa-list"></i>
                                                </Button>
                                            </InputGroup.Text>
                                            <Form.Control.Feedback type="invalid">Please enter a ticker</Form.Control.Feedback>
                                        </InputGroup>
                                        <div className="company-lookup">
                                            <Link className="ticker-link" onClick={() => setShowTickerLookupModal(true)}>Find Ticker(s) <i className="fa fa-search"></i></Link>
                                        </div>
                                    </Form.Group>}
                                </Col>
                                <Col md={2}>
                                    <GroupVisibleComponent groups={[USER_GROUPS.ADMIN]}>
                                        <Form.Group controlId="surveyYear">
                                            <Form.Label>Survey Year</Form.Label>
                                            <Form.Select
                                                type="number"
                                                value={surveyYear}
                                                onChange={(e) => setSurveyYear(e.target.value)}
                                            >
                                                <option value={2026}>2026</option>
                                                <option value={2025}>2025</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </GroupVisibleComponent>
                                </Col>
                            </Row>
                            {selectedSurveyTemplateId && <><Row>
                                <Col md={3}>
                                    <Form.Group controlId="primaryFilterType">
                                        <Form.Label>Filter Type</Form.Label>
                                        <Form.Select
                                            as="select"
                                            value={primaryFilterType}
                                            onChange={(e) => handleSetPrimaryFilterType(e.target.value)}
                                        >
                                            <option value="">No Filter</option>
                                            <option value="REVENUE">Revenue</option>
                                            <option value="MARKET_CAP">Market Cap</option>
                                            <option value="TOTAL_ASSETS">Total Assets</option>
                                            <option value="TOTAL_EMPLOYEES">Total Employees</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                {primaryFilterType && <><Col md={3}>
                                    <Form.Group controlId="primaryLowerBound">
                                        <Form.Label>{primaryFilterType === "TOTAL_EMPLOYEES" ? "Lower Bound" : "Lower Bound ($M)"}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={primaryLowerBoundDisplay}
                                            onChange={(e) => handleSetPrimaryLowerBound(e.target.value)}
                                            isInvalid={primaryFilterInvalid}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            Upper Bound must be less than Lower Bound
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                    <Col md={3}>
                                        <Form.Group controlId="primaryUpperBound">
                                            <Form.Label>{primaryFilterType === "TOTAL_EMPLOYEES" ? "Upper Bound" : "Upper Bound ($M)"}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={primaryUpperBoundDisplay}
                                                onChange={(e) => handleSetPrimaryUpperBound(e.target.value)}
                                                isInvalid={primaryFilterInvalid}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Upper Bound must be less than Lower Bound
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group controlId="filterOperator">
                                            <Form.Label>Secondary Filter</Form.Label>
                                            <Form.Select
                                                as="select"
                                                value={filterOperator}
                                                onChange={(e) => handleSetFilterOperator(e.target.value)}
                                            >
                                                <option value="">No Secondary Filter</option>
                                                <option value="AND">And</option>
                                                <option value="OR">Or</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </>}

                            </Row>
                                {primaryFilterType && filterOperator && <Row>
                                    <Col md={3}>
                                        <Form.Group controlId="secondaryFilterType">
                                            <Form.Label>Filter Type</Form.Label>
                                            <Form.Select
                                                as="select"
                                                value={secondaryFilterType}
                                                onChange={(e) => setSecondaryFilterType(e.target.value)}
                                            >
                                                <option value="REVENUE">Revenue</option>
                                                <option value="MARKET_CAP">Market Cap</option>
                                                <option value="TOTAL_ASSETS">Total Assets</option>
                                                <option value="TOTAL_EMPLOYEES">Total Employees</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group controlId="secondaryLowerBound">
                                            <Form.Label>{secondaryFilterType === "TOTAL_EMPLOYEES" ? "Lower Bound" : "Lower Bound ($M)"}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={secondaryLowerBoundDisplay}
                                                onChange={(e) => handleSetSecondaryLowerBound(e.target.value)}
                                                isInvalid={secondaryFilterInvalid}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Upper Bound must be less than Lower Bound
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group controlId="secondaryUpperBound">
                                            <Form.Label>{secondaryFilterType === "TOTAL_EMPLOYEES" ? "Upper Bound" : "Upper Bound ($M)"}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={secondaryUpperBoundDisplay}
                                                onChange={(e) => handleSetSecondaryUpperBound(e.target.value)}
                                                isInvalid={secondaryFilterInvalid}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Upper Bound must be less than Lower Bound
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>}
                            </>}
                            {isAdminUser && selectedSurveyTemplateId &&
                                <Form.Group controlId="excludeDefaultBreakouts">
                                    <Form.Check type="checkbox" label="Exclude Default Breakouts" checked={excludeDefaultBreakouts} onChange={(e) => setExcludeDefaultBreakouts(e.target.checked)} />
                                </Form.Group>}
                            <div className="survey-actions-container">
                                {selectedSurveyTemplateId ? <div className="survey-actions">
                                    <div className="survey-actions-row">
                                        {searching ? <Button variant="primary" disabled>
                                            Searching...
                                            <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                                        </Button> : <Button variant="primary" type="submit">
                                            Search
                                        </Button>}
                                        {isAdminUser && <Button variant="secondary" onClick={() => setShowUploadModal(true)}>
                                            Upload
                                        </Button>
                                        }
                                        {surveyData.length > 0 && isAdminUser &&
                                            <>
                                                {downloadingInput ?
                                                    <Button
                                                        variant="primaryr btn-download toolbar-btn"
                                                        disabled={true}>
                                                        Downloading...
                                                        <Spinner
                                                            as="span"
                                                            animation="grow"
                                                            size="sm"
                                                            role="status"
                                                            aria-hidden="true"
                                                        />
                                                    </Button> :
                                                    <Button
                                                        variant="primary btn-download toolbar-btn"
                                                        disabled={selectedSurveyRows.size === 0}
                                                        onClick={handleInputDataDownload}>
                                                        INPUT DATA
                                                    </Button>}
                                            </>
                                        }
                                    </div>
                                    {surveyData.length > 0 &&
                                        <>
                                            <div className='survey-actions-download-group'>
                                                <div className="survey-actions-download-label">Custom Cut Download</div>
                                                <div className="survey-actions-row">
                                                    <GroupVisibleComponent groups={[USER_GROUPS.ADMIN, USER_GROUPS.INTERNAL]}>
                                                        <Button
                                                            variant="secondary btn-download toolbar-btn"
                                                            className="preview-pill-button"
                                                            disabled={selectedSurveyRows.size === 0}
                                                            onClick={() => handleOpenSurveyDownloadModal("Survey_Output")}>
                                                            TABULAR DATA
                                                        </Button>
                                                        <Button
                                                            variant="secondary btn-download toolbar-btn"
                                                            className="preview-pill-button"
                                                            disabled={selectedSurveyRows.size === 0}
                                                            onClick={() => handleOpenSurveyDownloadModal("Survey_Regression")}>
                                                            REGRESSION DATA
                                                        </Button>
                                                    </GroupVisibleComponent>
                                                    <GroupVisibleComponent groups={[USER_GROUPS.CLIENT, USER_GROUPS.EXTERNAL]}>
                                                        <Button
                                                            variant="secondary btn-download toolbar-btn"
                                                            className="preview-pill-button"
                                                            disabled={selectedSurveyRows.size === 0}
                                                            onClick={() => handleOpenClientRequestModal("CUSTOM_CUT")}>
                                                            TABULAR DATA
                                                        </Button>
                                                        <Button
                                                            variant="secondary btn-download toolbar-btn"
                                                            className="preview-pill-button"
                                                            disabled={selectedSurveyRows.size === 0}
                                                            onClick={() => handleOpenClientRequestModal("REGRESSION")}>
                                                            REGRESSION DATA
                                                        </Button>
                                                    </GroupVisibleComponent>
                                                </div>
                                            </div>
                                        </>
                                    }
                                    <>
                                        <GroupVisibleComponent groups={[USER_GROUPS.ADMIN, USER_GROUPS.INTERNAL]}>
                                            <div className="survey-actions-preview-group">
                                                <div className="survey-actions-preview-label">Preview</div>
                                                <div className="survey-actions-row">
                                                    <>
                                                        <Button
                                                            variant="secondary btn-download toolbar-btn"
                                                            className="preview-pill-button"
                                                            disabled={selectedSurveyRows.size === 0}
                                                            onClick={handleShowBenchmarkPreviewModal}>
                                                            BENCHMARK TDC
                                                        </Button>

                                                        <Button
                                                            variant="secondary btn-download toolbar-btn"
                                                            className="preview-pill-button"
                                                            disabled={selectedSurveyRows.size === 0}
                                                            onClick={() => setShowSurveyDataPointsPreviewModal(true)}>
                                                            SAMPLE SIZES
                                                        </Button>
                                                        <Button
                                                            variant="secondary btn-download toolbar-btn"
                                                            className="preview-pill-button"
                                                            disabled={selectedSurveyRows.size === 0}
                                                            onClick={() => handleShowRegressionPreviewModal(REVENUE)}>
                                                            REVENUE REGRESSION
                                                        </Button>
                                                        <Button
                                                            variant="secondary btn-download toolbar-btn"
                                                            className="preview-pill-button"
                                                            disabled={selectedSurveyRows.size === 0}
                                                            onClick={() => handleShowRegressionPreviewModal(MARKET_CAP)}>
                                                            MARKET CAP REGRESSION
                                                        </Button>
                                                        <Button
                                                            variant="secondary btn-download toolbar-btn"
                                                            className="preview-pill-button"
                                                            disabled={selectedSurveyRows.size === 0}
                                                            onClick={() => handleShowRegressionPreviewModal(TOTAL_ASSETS)}>
                                                            TOTAL ASSETS REGRESSION
                                                        </Button>
                                                    </>
                                                </div>
                                            </div>
                                        </GroupVisibleComponent>
                                    </>
                                </div> : null}
                            </div>
                        </Col>
                        <Col md={5}>
                            {financialOverviewTable}
                        </Col>
                    </Row>
                </Form>
                <UploadModal templateId={selectedSurveyTemplateId} templateType={"Survey"} show={showUploadModal} onClose={() => setShowUploadModal(false)} />
                <SurveyDownloadOutputModal
                    show={showSurveyDownloadOutputModal && downloadType}
                    onClose={handleCloseSurveyDownloadModal}
                    compensationIds={selectedSurveyRows}
                    selectedTemplate={selectedSurveyTemplateSummary}
                    surveyYear={surveyYear}
                    excludeDefaultBreakouts={excludeDefaultBreakouts}
                    downloadType={downloadType}
                />
                <SurveyRegressionPreviewModal
                    show={showSurveyRegressionPreviewModal}
                    onClose={() => setShowSurveyRegressionPreviewModal(false)}
                    selectedSurveyRows={selectedSurveyRows}
                    selectedSurveyTemplateId={selectedSurveyTemplateId}
                    surveyYear={surveyYear}
                    regressionType={selectedRegressionType}
                />
                <SurveyDataPointsPreviewModal
                    show={showSurveyDataPointsPreviewModal}
                    onClose={() => setShowSurveyDataPointsPreviewModal(false)}
                    selectedSurveyRows={selectedSurveyRows}
                    selectedSurveyTemplateId={selectedSurveyTemplateId}
                    surveyYear={surveyYear}
                />
                <BenchmarkPreviewModal
                    show={showBenchmarkPreviewModal}
                    onClose={() => setShowBenchmarkPreviewModal(false)}
                    selectedSurveyRows={selectedSurveyRows}
                    selectedSurveyTemplateId={selectedSurveyTemplateId}
                    surveyYear={surveyYear}
                />
                <ClientRequestModal
                    show={showClientRequestModal}
                    onClose={() => handleCloseClientRequestModal()}
                    requestType={clientRequestType}
                    compensationIds={selectedSurveyRows}
                    surveyYear={surveyYear}
                />
                {error ? <Alert dismissible variant="danger">{error}</Alert> : null}
                {successMessage ? <Alert dismissible variant="success">{successMessage}</Alert> : null}
                {hasConflictingTicker && <TickerNameConflictModal
                    isOpen={showTickerNameConflictModal}
                    onClose={() => setShowTickerNameConflictModal(false)}
                    conflictingCompanies={conflictingCompanies}
                    selectedCompanyIds={selectedConflictingCompanyIds}
                    onSelectCompanyId={(companyIds) => handleConflictingCompanyIdSelection(companyIds)}
                    onSearch={() => retrieveSurveyData()}
                />
                }
                <div className="survey-data-grid-container">
                    {!searching && surveyData?.length > 0 && !isAdminUser ? <Alert dismissible variant="warning">Note: Private companies are excluded from survey data search results. Reach out to <b>data@fwcook.com</b> with any questions.</Alert> : null}
                    {!searching && noDataAvailable?.length > 0 ? <Alert dismissible variant="warning">No data available for: <b>{noDataAvailable.join(", ")}</b></Alert> : null}
                    {!searching && surveyData && surveyData.length > 0 && surveyDataGridColumns && surveyDataGridColumns.length > 0 ?
                        <CompensationGrid
                            templateTitle={selectedSurveyTemplateSummary.templateName}
                            columnDefinitions={surveyDataGridColumns}
                            compensations={surveyData}
                            rowKeyGetter={(row) => row.uniqueId}
                            selectedRows={selectedSurveyRows}
                            isGridExpanded={gridExpandedStatus}
                            handleGridResize={(isExpanded) => setGridExpandedStatus(isExpanded)}
                            onSelectedRowsChange={(selectedRows) => setSelectedSurveyRows(selectedRows)}
                            onBringToTop={() => handleBringSelectedDataToTop(surveyData)}
                        ></CompensationGrid>
                        : null}
                </div>
            </div>
        </div >
    );
};

export default SurveyDataConsole;
